import * as THREE from 'three';
import { TriangulationResult } from '../types';

/** 1 mm = 0.01 Three.js units (1 unit = 100 mm) */
const S = 0.01;

/**
 * Buduje tablicę wybranych trójkątów z etykietami regionu.
 * isDome=true → trójkąt leży wewnątrz otworu szablonu (kopułka wydmuchu).
 */
function selectTrianglesWithLabels(
  tri: TriangulationResult,
  showFull: boolean,
): Array<{ abc: [number, number, number]; isDome: boolean }> {
  const result: Array<{ abc: [number, number, number]; isDome: boolean }> = [];
  for (let ti = 0; ti < tri.triangles.length; ti++) {
    if (!showFull && tri.triCutLabels.length > 0 && tri.triCutLabels[ti] !== 0) continue;
    result.push({
      abc:    tri.triangles[ti],
      isDome: (tri.triLabels[ti] ?? 0) > 0,
    });
  }
  return result;
}

/**
 * Wyznacza tablicę duplikatów wierzchołków granicznych.
 * Wierzchołki graniczne to te, które należą jednocześnie do trójkątów płaskich (materiał)
 * i trójkątów kopułki (otwór). Dla nich tworzymy kopie, dzięki czemu computeVertexNormals()
 * daje osobne normalne po stronie płaskiej (0,0,±1) i po stronie kopułki (nachylone).
 *
 * Zwraca Int32Array o długości n: boundaryDupIdx[i] = indeks kopii lub -1.
 */
function buildBoundaryDupIdx(
  n: number,
  selTris: Array<{ abc: [number, number, number]; isDome: boolean }>,
): { boundaryDupIdx: Int32Array; numDups: number } {
  const inFlat = new Uint8Array(n);
  const inDome = new Uint8Array(n);
  for (const { abc: [a, b, c], isDome } of selTris) {
    if (isDome) { inDome[a] = inDome[b] = inDome[c] = 1; }
    else        { inFlat[a] = inFlat[b] = inFlat[c] = 1; }
  }
  const boundaryDupIdx = new Int32Array(n).fill(-1);
  let numDups = 0;
  for (let i = 0; i < n; i++) {
    if (inFlat[i] && inDome[i]) boundaryDupIdx[i] = n + numDups++;
  }
  return { boundaryDupIdx, numDups };
}

/**
 * Górna powierzchnia arkusza (z = 0, normalna w górę +Z).
 *
 * Wierzchołki graniczne (na brzegu otworu) są zduplikowane:
 *   – oryginalne indeksy (0..n-1)  → używane przez trójkąty kopułki
 *   – kopie         (n..n+k-1)     → używane przez trójkąty płaskiego materiału
 * Dzięki temu computeVertexNormals() daje poprawne normalne bez artefaktów cieniowania.
 *
 * geo.userData.numOrigVerts   = n          (liczba oryginalnych wierzchołków)
 * geo.userData.boundaryDupIdx = Int32Array  (mapowanie: oryg → kopia lub -1)
 */
export function buildTopGeometry(
  tri: TriangulationResult,
  widthMm: number,
  heightMm: number,
  showFull = true,
): THREE.BufferGeometry {
  const hw = widthMm  / 2;
  const hh = heightMm / 2;
  const { vertices } = tri;
  const n = vertices.length;

  const selTris = selectTrianglesWithLabels(tri, showFull);
  const { boundaryDupIdx, numDups } = buildBoundaryDupIdx(n, selTris);

  const totalVerts = n + numDups;
  const pos = new Float32Array(totalVerts * 3);
  const nrm = new Float32Array(totalVerts * 3);
  const uv  = new Float32Array(totalVerts * 2);

  for (let i = 0; i < n; i++) {
    const { x, y } = vertices[i];
    pos[i*3]     = x * S;
    pos[i*3 + 1] = y * S;
    pos[i*3 + 2] = 0;
    nrm[i*3 + 2] = 1;
    uv[i*2]      = (x + hw) / widthMm;
    uv[i*2 + 1]  = (y + hh) / heightMm;
  }
  // Duplikaty wierzchołków granicznych – te same pozycje/UV
  for (let i = 0; i < n; i++) {
    const di = boundaryDupIdx[i];
    if (di < 0) continue;
    pos[di*3]     = pos[i*3];
    pos[di*3 + 1] = pos[i*3 + 1];
    pos[di*3 + 2] = pos[i*3 + 2];
    nrm[di*3 + 2] = 1;
    uv[di*2]      = uv[i*2];
    uv[di*2 + 1]  = uv[i*2 + 1];
  }

  const idx = new Uint32Array(selTris.length * 3);
  for (let ti = 0; ti < selTris.length; ti++) {
    const { abc: [a, b, c], isDome } = selTris[ti];
    if (isDome) {
      // Trójkąt kopułki: oryginalne indeksy → dostanie nachylone normalne od computeVertexNormals()
      idx[ti*3] = a;  idx[ti*3 + 1] = b;  idx[ti*3 + 2] = c;
    } else {
      // Trójkąt płaski: dla wierzchołków granicznych użyj kopii → dostanie normalną (0,0,1)
      idx[ti*3]     = boundaryDupIdx[a] >= 0 ? boundaryDupIdx[a] : a;
      idx[ti*3 + 1] = boundaryDupIdx[b] >= 0 ? boundaryDupIdx[b] : b;
      idx[ti*3 + 2] = boundaryDupIdx[c] >= 0 ? boundaryDupIdx[c] : c;
    }
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('normal',   new THREE.BufferAttribute(nrm, 3));
  geo.setAttribute('uv',       new THREE.BufferAttribute(uv,  2));
  geo.setIndex(new THREE.BufferAttribute(idx, 1));
  // Zachowaj mapowanie do synchronizacji kopii w updateSimPositions
  geo.userData.numOrigVerts   = n;
  geo.userData.boundaryDupIdx = boundaryDupIdx;
  return geo;
}

/**
 * Dolna powierzchnia arkusza (z = -thickness, normalna w dół -Z).
 * Odwrócona kolejność wierzchołków trójkątów (CW z +Z = CCW z -Z).
 * Tak samo jak buildTopGeometry – wierzchołki graniczne są zduplikowane.
 */
export function buildBottomGeometry(
  tri: TriangulationResult,
  widthMm: number,
  heightMm: number,
  thicknessMm: number,
  showFull = true,
): THREE.BufferGeometry {
  const hw = widthMm  / 2;
  const hh = heightMm / 2;
  const tz = -thicknessMm * S;
  const { vertices } = tri;
  const n = vertices.length;

  const selTris = selectTrianglesWithLabels(tri, showFull);
  const { boundaryDupIdx, numDups } = buildBoundaryDupIdx(n, selTris);

  const totalVerts = n + numDups;
  const pos = new Float32Array(totalVerts * 3);
  const nrm = new Float32Array(totalVerts * 3);
  const uv  = new Float32Array(totalVerts * 2);

  for (let i = 0; i < n; i++) {
    const { x, y } = vertices[i];
    pos[i*3]     = x * S;
    pos[i*3 + 1] = y * S;
    pos[i*3 + 2] = tz;
    nrm[i*3 + 2] = -1;
    uv[i*2]      = (x + hw) / widthMm;
    uv[i*2 + 1]  = (y + hh) / heightMm;
  }
  for (let i = 0; i < n; i++) {
    const di = boundaryDupIdx[i];
    if (di < 0) continue;
    pos[di*3]     = pos[i*3];
    pos[di*3 + 1] = pos[i*3 + 1];
    pos[di*3 + 2] = pos[i*3 + 2];
    nrm[di*3 + 2] = -1;
    uv[di*2]      = uv[i*2];
    uv[di*2 + 1]  = uv[i*2 + 1];
  }

  // Odwrócona kolejność: [a, c, b]
  const idx = new Uint32Array(selTris.length * 3);
  for (let ti = 0; ti < selTris.length; ti++) {
    const { abc: [a, b, c], isDome } = selTris[ti];
    if (isDome) {
      idx[ti*3] = a;  idx[ti*3 + 1] = c;  idx[ti*3 + 2] = b;
    } else {
      const ia = boundaryDupIdx[a] >= 0 ? boundaryDupIdx[a] : a;
      const ib = boundaryDupIdx[b] >= 0 ? boundaryDupIdx[b] : b;
      const ic = boundaryDupIdx[c] >= 0 ? boundaryDupIdx[c] : c;
      idx[ti*3] = ia;  idx[ti*3 + 1] = ic;  idx[ti*3 + 2] = ib;
    }
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('normal',   new THREE.BufferAttribute(nrm, 3));
  geo.setAttribute('uv',       new THREE.BufferAttribute(uv,  2));
  geo.setIndex(new THREE.BufferAttribute(idx, 1));
  geo.userData.numOrigVerts   = n;
  geo.userData.boundaryDupIdx = boundaryDupIdx;
  return geo;
}

/**
 * Boczne ścianki arkusza – tylko zewnętrzny obwód.
 * Krawędź jest graniczna gdy pojawia się dokładnie raz wśród WSZYSTKICH
 * trójkątów (materiał + obszary otworów szablonu). Krawędzie wewnętrzne
 * (między obszarem materiału a obszarem otworu) pojawiają się dwukrotnie
 * i zostają pominięte – boki powstają wyłącznie na zewnętrznym obwodzie arkusza.
 */
export function buildSidesGeometry(
  tri: TriangulationResult,
  thicknessMm: number,
  showFull = true,
): THREE.BufferGeometry {
  const tz = -thicknessMm * S;
  const { vertices } = tri;
  const triangles = selectTrianglesWithLabels(tri, showFull).map(t => t.abc);

  // nodeLabels[i] === 1 oznacza wierzchołek na zewnętrznym obwodzie PSLG (prostokąt materiału).
  // Gdy showFull=false (tryb wyciętego obszaru), krawędzie leżące na tym prostokącie
  // mogą fałszywie mieć count===1, jeśli sąsiedni trójkąt po stronie „zewnętrznej" jest
  // wycięty. Skutek: cienka ramka wokół całego arkusza (błąd wizualny).
  // Pomijamy te krawędzie – zewnętrzny obwód i tak jest widoczny przez boczną ściankę
  // arkusza wyznaczaną z trójkątów przy krawędzi bez obszarów wyciętych.
  const outerLabels = tri.nodeLabels;

  // Zlicz wystąpienia każdej krawędzi wśród WSZYSTKICH trójkątów
  const edgeCount = new Map<string, number>();
  const edgeFirst = new Map<string, [number, number]>();

  for (const [a, b, c] of triangles) {
    for (const [u, v] of [[a, b], [b, c], [c, a]] as [number, number][]) {
      const key = u < v ? `${u}_${v}` : `${v}_${u}`;
      edgeCount.set(key, (edgeCount.get(key) ?? 0) + 1);
      if (!edgeFirst.has(key)) edgeFirst.set(key, [u, v]);
    }
  }

  const posArr: number[] = [];
  const idxArr: number[] = [];

  for (const [key, count] of Array.from(edgeCount)) {
    if (count !== 1) continue;           // tylko krawędzie zewnętrzne
    const [u, v] = edgeFirst.get(key)!;

    // Gdy showFull=false, pomijamy krawędzie leżące na zewnętrznym obwodzie PSLG
    // (obie końcówki mają nodeLabels===1). Chroni przed fałszywą ramką dookoła arkusza.
    if (!showFull && outerLabels[u] === 1 && outerLabels[v] === 1) continue;
    const xu = vertices[u].x * S, yu = vertices[u].y * S;
    const xv = vertices[v].x * S, yv = vertices[v].y * S;

    const base = posArr.length / 3;
    // 4 wierzchołki: topU, topV, bottomV, bottomU
    posArr.push(xu, yu, 0,   xv, yv, 0,   xv, yv, tz,   xu, yu, tz);
    // Dwa trójkąty tworzące quad
    idxArr.push(base, base + 1, base + 2,  base, base + 2, base + 3);
  }

  const geo = new THREE.BufferGeometry();
  if (posArr.length > 0) {
    geo.setAttribute('position',
      new THREE.BufferAttribute(new Float32Array(posArr), 3));
    geo.setIndex(
      new THREE.BufferAttribute(new Uint32Array(idxArr), 1));
    geo.computeVertexNormals();
  }
  return geo;
}
